import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { useLang } from "@/i18n";
import { MaskedLines } from "@/lib/motion";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden flex items-end">
      {/* Parallax background image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src="/images/hero.jpg"
          alt="Componente in metallo lavorato da robot Olmi"
          className="w-full h-full object-cover"
          style={{ filter: "grayscale(0.3) brightness(0.5) contrast(1.05)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-[#0A0A0A]/70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_40%,rgba(212,175,55,0.10),transparent_60%)]" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="font-accent text-xs md:text-sm tracking-[0.3em] uppercase text-[#D4AF37]" data-testid="hero-overline">
            {t.hero.since}
          </span>
        </motion.div>

        <h1 className="font-display font-semibold uppercase tracking-tighter leading-[0.92] text-[15vw] sm:text-[12vw] lg:text-[9.5vw] text-white" data-testid="hero-title">
          <MaskedLines lines={t.hero.lines} />
        </h1>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="lg:col-span-6 text-base md:text-lg text-zinc-300 leading-relaxed max-w-xl"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="lg:col-span-6 flex flex-wrap items-center gap-4 lg:justify-end"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="btn-fill inline-flex items-center gap-3 border border-[#D4AF37] px-7 py-4 font-accent text-sm uppercase tracking-widest text-[#D4AF37] hover:text-black"
            >
              {t.hero.cta} <ArrowDownRight size={18} />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="font-accent text-sm uppercase tracking-widest text-zinc-300 hover:text-white link-underline"
            >
              {t.hero.cta2}
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
